import React, {useEffect, useState} from "react";
import articlesService from "../../../services/articles.service";
import {useForm} from "react-hook-form";
import {toast} from "react-toastify";
import Swal from 'sweetalert2';
import {Link} from "react-router-dom";
import {motion, AnimatePresence} from 'framer-motion';

export default function ManageCategory() {
    const [categoryList, setCategoryList] = useState([]);
    const [expandedNodes, setExpandedNodes] = useState(new Set(["root"]));
    const {register, handleSubmit, reset} = useForm();

    useEffect(() => {
        getCategory();
    }, []);

    const getCategory = () => {
        articlesService.getArticlesCategory()
            .then(res => setCategoryList(res.data))
            .catch(console.error);
    };

    const convertToHierarchy = (array, parentId = 0) => {
        return array
            .filter(item => item.parentCategoryId === parentId)
            .map(item => ({
                id: item.id.toString(),
                name: item.title,
                children: convertToHierarchy(array, item.id)
            }));
    };

    const toggleNode = (nodeId) => {
        setExpandedNodes(prev => {
            const newSet = new Set(prev);
            if (newSet.has(nodeId)) {
                newSet.delete(nodeId);
            } else {
                newSet.add(nodeId);
            }
            return newSet;
        });
    };

    const renderTree = (nodes) => {
        const isExpanded = expandedNodes.has(nodes.id);
        const hasChildren = nodes.children?.length > 0;

        return (
            <div key={nodes.id} className="tree-node mb-1">
                <div
                    className="d-flex align-items-center py-1 px-2 rounded hover-bg-light"
                    onClick={() => hasChildren && toggleNode(nodes.id)}
                    style={{cursor: hasChildren ? "pointer" : "default"}}
                >
                    {hasChildren ? (
                        <i
                            className={`bi me-2 ${
                                isExpanded ? "bi-chevron-down" : "bi-chevron-right"
                            }`}
                        ></i>
                    ) : (
                        <span
                            className="me-2"
                            style={{width: "16px", display: "inline-block"}}
                        ></span>
                    )}
                    <span className="flex-grow-1">{nodes.name}</span>
                    {hasChildren && (
                        <span className="badge bg-secondary ms-2">
                            {nodes.children.length}
                        </span>
                    )}
                </div>

                {hasChildren && (
                    <AnimatePresence>
                        {isExpanded && (
                            <motion.div
                                initial={{height: 0, opacity: 0}}
                                animate={{height: "auto", opacity: 1}}
                                exit={{height: 0, opacity: 0}}
                                transition={{duration: 0.2, ease: "easeInOut"}}
                                className="ps-3 ms-2 border-start"
                            >
                                {nodes.children.map(renderTree)}
                            </motion.div>
                        )}
                    </AnimatePresence>
                )}
            </div>
        );
    };


    const onSubmit = async (formData) => {
        try {
            await articlesService.saveArticlesCategory(formData);
            toast.success("Category added successfully!");
            reset();
            getCategory();
        } catch (err) {
            toast.error("Error adding category");
            console.error(err);
        }
    };

    const handleDelete = async (formData) => {
        const result = await Swal.fire({
            title: 'Delete Category?',
            html: `You're about to delete <strong>${categoryList.find(c => c.id == formData.categoryid)?.title}</strong>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete it!',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            try {
                await articlesService.deleteCategory(formData.categoryid);
                toast.success("Category deleted successfully!");
                getCategory();
            } catch (err) {
                toast.error("Error deleting category");
                console.error(err);
            }
        }
    };

    const treeData = {
        id: "root",
        name: "All Categories",
        children: convertToHierarchy(categoryList)
    };

    return (
        <div className="container container-xxl">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex align-items-center">
                    <i className="bi bi-diagram-3 fs-3 text-primary me-3"></i>
                    <h1 className="h4 mb-0 fw-semibold">Article Categories</h1>
                </div>
                <Link to="/articles" className="btn btn-outline-secondary btn-sm">
                    <i className="bi bi-chevron-left me-1"></i> Back to Articles
                </Link>
            </div>

            <div className="row g-4">
                {/* Category Tree - HTML Version */}
                <div className="col-lg-6">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-header bg-white border-bottom">
                            <h5 className="mb-0 fw-semibold">
                                <i className="bi bi-list-tree me-2"></i>Category Structure
                            </h5>
                        </div>
                        <div className="card-body">
                            {categoryList.length > 0 ? (
                                <div className="tree-view" style={{minHeight: '400px'}}>
                                    {renderTree(treeData)}
                                </div>
                            ) : (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-lg-6 position-relative">
                    {/* Lock Overlay */}
                    <div
                        className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center"
                        style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.85)',
                            zIndex: 10,
                            borderRadius: '0.375rem' // Match card border radius
                        }}>
                        <i className="bi bi-lock-fill text-muted" style={{fontSize: '3rem'}}></i>
                        <p className="mt-2 text-muted fw-semibold">Category Management Locked</p>
                        <p className="text-center text-muted px-4">Contact administrator<br/>to modify categories</p>
                    </div>
                    {/* Action Panels */}
                    <div className="col-lg-6">
                        {/* Add Category */}
                        <div className="card border-0 shadow-sm mb-4">
                            <div className="card-header bg-white border-bottom">
                                <h5 className="mb-0 fw-semibold">
                                    <i className="bi bi-plus-circle me-2"></i>Add New Category
                                </h5>
                            </div>
                            <div className="card-body">
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Parent Category</label>
                                        <select
                                            {...register("parentCategoryId")}
                                            className="form-select"
                                        >
                                            <option value="0">-- Root Category --</option>
                                            {categoryList.map(item => (
                                                <option key={item.id} value={item.id}>
                                                    {item.title}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Category Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            {...register("title", {required: true})}
                                            placeholder="Enter category name"
                                        />
                                    </div>
                                    <div className="d-grid">
                                        <button type="submit" className="btn btn-primary">
                                            <i className="bi bi-save me-2"></i>Save Category
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>

                        {/* Delete Category */}
                        <div className="card border-0 shadow-sm">
                            <div className="card-header bg-white border-bottom">
                                <h5 className="mb-0 fw-semibold text-danger">
                                    <i className="bi bi-trash me-2"></i>Delete Category
                                </h5>
                            </div>
                            <div className="card-body">
                                <form onSubmit={handleSubmit(handleDelete)}>
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Select Category</label>
                                        <select
                                            {...register("categoryid", {required: true})}
                                            className="form-select"
                                        >
                                            {categoryList.map(item => (
                                                <option key={item.id} value={item.id}>
                                                    {item.title} (ID: {item.id})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="d-grid">
                                        <button type="submit" className="btn btn-danger">
                                            <i className="bi bi-trash me-2"></i>Delete Category
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}